import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Lightbulb, X, Loader2, Check } from 'lucide-react';

const ScrambleGame = ({ rounds, onWin, onExit }) => {
  const [roundIdx, setRoundIdx] = useState(0);
  const round = rounds[roundIdx] || {};
  const word = (round.word || '').toUpperCase();
  const scrambled = (round.scrambled || '').toUpperCase();
  const hint = round.hint || '';

  const [guess, setGuess] = useState('');
  const [status, setStatus] = useState('playing'); // playing | won | gave-up
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    setGuess('');
    setStatus('playing');
    setShowHint(false);
  }, [roundIdx]);

  const submit = (e) => {
    e?.preventDefault?.();
    if (status !== 'playing') return;
    if (guess.trim().toUpperCase() === word) setStatus('won');
  };

  const giveUp = () => setStatus('gave-up');

  const next = () => {
    if (roundIdx + 1 >= rounds.length) {
      // Need at least 3 wins to claim — track via parent. Here we just signal win attempt completed.
      onWin?.();
    } else {
      setRoundIdx((i) => i + 1);
    }
  };

  if (!word) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#FF007F] animate-spin" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="scramble-game">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
          Level {roundIdx + 1} of {rounds.length}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
          {word.length} letters
        </span>
      </div>

      {/* Scrambled letters as tiles */}
      <div className="flex flex-wrap justify-center gap-1.5 py-4 px-2 rounded-2xl bg-white/[0.03] border border-white/10" data-testid="scramble-tiles">
        {scrambled.split('').map((c, i) => (
          <span
            key={`tile-${roundIdx}-${i}`}
            className="w-9 h-11 sm:w-10 sm:h-12 flex items-center justify-center rounded-lg bg-[#FF007F]/15 border border-[#FF007F]/30 text-white font-bold text-lg font-['JetBrains_Mono']"
          >
            {c}
          </span>
        ))}
      </div>

      {/* Hint */}
      <div>
        {showHint ? (
          <div className="flex items-start gap-2 text-sm text-gray-300 bg-[#FFD700]/8 border border-[#FFD700]/20 rounded-xl px-3 py-2" data-testid="scramble-hint">
            <Lightbulb className="w-3.5 h-3.5 text-[#FFD700] flex-shrink-0 mt-0.5" />
            <span>{hint}</span>
          </div>
        ) : (
          status === 'playing' && (
            <button
              onClick={() => setShowHint(true)}
              className="text-xs text-[#FFD700] hover:underline flex items-center gap-1"
              data-testid="scramble-reveal-hint"
            >
              <Lightbulb className="w-3 h-3" /> Reveal hint
            </button>
          )
        )}
      </div>

      {/* Guess input */}
      {status === 'playing' && (
        <form onSubmit={submit} className="space-y-3">
          <Input
            value={guess}
            onChange={(e) => setGuess(e.target.value.toUpperCase().slice(0, 12))}
            placeholder="Type your answer..."
            className="bg-black/40 border-white/15 text-white h-12 rounded-xl text-center tracking-[0.3em] font-['JetBrains_Mono'] uppercase"
            data-testid="scramble-input"
            autoFocus
          />
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={giveUp}
              className="h-11 border border-white/15 text-white rounded-xl text-xs font-bold tracking-wider"
              data-testid="scramble-skip"
            >
              SKIP
            </Button>
            <Button
              type="submit"
              disabled={!guess.trim()}
              className="h-11 bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-xl text-xs font-bold tracking-wider disabled:opacity-40"
              data-testid="scramble-submit"
            >
              SUBMIT
            </Button>
          </div>
        </form>
      )}

      {status === 'won' && (
        <div className="text-center p-5 rounded-2xl bg-[#39FF14]/10 border border-[#39FF14]/30" data-testid="scramble-round-won">
          <Check className="w-7 h-7 text-[#39FF14] mx-auto mb-1.5" />
          <p className="text-[#39FF14] font-bold text-lg font-['Outfit']">Solved!</p>
          <p className="text-gray-400 text-sm mt-1">The word was <span className="text-white font-mono">{word}</span></p>
          <Button onClick={next} className="mt-4 bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-xl px-6" data-testid="scramble-next">
            {roundIdx + 1 >= rounds.length ? 'CLAIM REWARD' : 'NEXT LEVEL'}
          </Button>
        </div>
      )}
      {status === 'gave-up' && (
        <div className="text-center p-5 rounded-2xl bg-[#FF3B3B]/10 border border-[#FF3B3B]/30" data-testid="scramble-round-skipped">
          <p className="text-[#FF3B3B] font-bold text-lg font-['Outfit']">Skipped</p>
          <p className="text-gray-400 text-sm mt-1">The word was <span className="text-white font-mono">{word}</span></p>
          <Button onClick={next} variant="ghost" className="mt-4 border border-white/15 text-white rounded-xl px-6" data-testid="scramble-skipped-next">
            {roundIdx + 1 >= rounds.length ? 'BACK TO GAMES' : 'TRY NEXT LEVEL'}
          </Button>
        </div>
      )}

      <button
        onClick={onExit}
        className="w-full text-xs text-gray-500 hover:text-gray-300 inline-flex items-center justify-center gap-1.5 pt-2"
        data-testid="scramble-quit"
      >
        <X className="w-3 h-3" /> Quit game
      </button>
    </div>
  );
};

export default ScrambleGame;
